import { Button, buttonVariants } from '@/components/ui/button'
import { ActionSurface } from '@/components/patterns/action-surface'
import { selectableSurfaceVariants } from '@/components/patterns/selectable-surface'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ToggleButton } from '@/components/ui/toggle-button'
import { Typography } from '@/components/ui/typography'
import { useDroppable } from '@dnd-kit/core'
import {
  type AnimateLayoutChanges,
  defaultAnimateLayoutChanges,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Check, Eye, EyeOff, GripVertical, Palette, Plus, Settings2 } from 'lucide-react'
import {
  type PointerEvent,
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useRef,
  useState,
  type WheelEvent,
} from 'react'
import { toast } from 'sonner'
import queryClient from '@/lib/queryClient'
import {
  categoryQueryKeys,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from '@/lib/services/categories/categories.query'
import type {
  Category,
  CategoryPosColor,
  UpdateCategoryInput,
} from '@/lib/services/categories/categories.types'
import {
  getCategoryColorValue,
  getCategorySlotId,
  getCategorySortOrder,
  getFirstAvailableCategorySortOrder,
  isCategorySortOrderOpen,
  sortPosCategories,
  updateCategoryQueryData,
} from '@/lib/pos/category-shortcuts'
import { cn, getErrorMessage } from '@/lib/utils'
import { CATEGORY_TILE_COLOR_OPTIONS, getCategoryTileColorOption } from './category-tile-colors'

const CATEGORY_TILE_CONTROL_VISIBILITY_CLASSES =
  'pointer-events-none opacity-0 group-has-[:focus-visible]/category:pointer-events-auto group-has-[:focus-visible]/category:opacity-100 group-hover/category:pointer-events-auto group-hover/category:opacity-100 [@media(pointer:coarse)]:pointer-events-auto [@media(pointer:coarse)]:opacity-100'

function getCategoryDragHandleClasses(
  isDragging: boolean,
  { alwaysVisible = false }: { alwaysVisible?: boolean } = {}
): string {
  return cn(
    'absolute right-2 top-2 touch-none',
    isDragging ? 'cursor-grabbing' : 'cursor-grab active:cursor-grabbing',
    !alwaysVisible && CATEGORY_TILE_CONTROL_VISIBILITY_CLASSES
  )
}

function getCategoryColorButtonClasses({
  isDragging = false,
  alwaysVisible = false,
}: { isDragging?: boolean; alwaysVisible?: boolean } = {}): string {
  return cn(
    'absolute right-2 bottom-2',
    isDragging ? 'cursor-grabbing' : 'cursor-pointer',
    !alwaysVisible && CATEGORY_TILE_CONTROL_VISIBILITY_CLASSES
  )
}

function stopCategoryTileControlEvent(event: SyntheticEvent): void {
  event.stopPropagation()
}

function animateCategoryTileLayoutChanges(args: Parameters<AnimateLayoutChanges>[0]): boolean {
  if (!args.isSorting) {
    return false
  }

  return defaultAnimateLayoutChanges(args)
}

function CategoryTileName({ category }: { category: Pick<Category, 'name'> }) {
  return (
    <Typography variant="h5" data-pos-category-name>
      {category.name}
    </Typography>
  )
}

export function CategoryTileDragOverlay({
  category,
  dragSize,
}: {
  category: Category
  /** The dragged tile's size when the drag started. */
  dragSize: { width: number; height: number } | null
}) {
  return (
    <div
      data-pos-category-drag-overlay
      className="relative min-h-24"
      style={{
        width: dragSize?.width ?? 224,
        height: dragSize?.height ?? 96,
      }}
    >
      <div
        className={cn(
          selectableSurfaceVariants({ appearance: 'bare', padding: 'default', radius: 'control' }),
          'pointer-events-none flex h-full min-h-24 w-full cursor-grabbing flex-col justify-end pr-12',
          getCategoryTileColorOption(category).className
        )}
      >
        <CategoryTileName category={category} />
      </div>
      <div
        className={cn(
          buttonVariants({ variant: 'outline', size: 'icon-xs' }),
          getCategoryDragHandleClasses(true, { alwaysVisible: true }),
          'pointer-events-none'
        )}
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <div
        className={cn(
          buttonVariants({ variant: 'outline', size: 'icon-xs' }),
          getCategoryColorButtonClasses({ isDragging: true, alwaysVisible: true }),
          'pointer-events-none'
        )}
      >
        <Palette className="h-4 w-4" />
      </div>
    </div>
  )
}

function CategoryTileColorPicker({
  category,
  disabled,
  onChangeColor,
}: {
  category: Category
  disabled: boolean
  onChangeColor: (category: Category, color: CategoryPosColor) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const selectedColor = getCategoryTileColorOption(category).value

  const handleSelectColor = (color: CategoryPosColor) => {
    if (category.pos_color !== color) {
      void onChangeColor(category, color)
    }

    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon-xs"
          className={getCategoryColorButtonClasses()}
          aria-label={`Change ${category.name} color`}
          title="Change tile color"
          disabled={disabled}
          onClick={stopCategoryTileControlEvent}
          onPointerDown={stopCategoryTileControlEvent}
        >
          <Palette className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        size="auto"
        padding="compact"
        onClick={stopCategoryTileControlEvent}
        onPointerDown={stopCategoryTileControlEvent}
      >
        <div className="grid grid-cols-3 gap-2">
          {CATEGORY_TILE_COLOR_OPTIONS.map((option) => {
            const isSelected = selectedColor === option.value

            return (
              <ToggleButton
                key={option.value}
                type="button"
                aria-label={`${option.label} color`}
                pressed={isSelected}
                variant="outline"
                pressedVariant="outline"
                size="icon"
                className={cn(
                  'size-9 rounded-md hover:ring-2 hover:ring-selection-border data-[state=on]:ring-2 data-[state=on]:ring-ring',
                  option.className
                )}
                onClick={() => handleSelectColor(option.value)}
              >
                {isSelected ? <Check className="h-4 w-4 text-current drop-shadow" /> : null}
              </ToggleButton>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function SortableCategoryTile({
  category,
  disabled,
  isSortingActive,
  onAdd,
  onChangeColor,
}: {
  category: Category
  disabled: boolean
  isSortingActive: boolean
  onAdd: (category: Category) => void
  onChangeColor: (category: Category, color: CategoryPosColor) => Promise<void>
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(category.id),
    disabled,
    animateLayoutChanges: animateCategoryTileLayoutChanges,
  })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: isDragging ? undefined : CSS.Transform.toString(transform),
        transition: isSortingActive ? transition : undefined,
      }}
      className={cn('group/category relative min-h-24', isDragging && 'opacity-0')}
    >
      <ActionSurface
        appearance="bare"
        padding="default"
        radius="control"
        onClick={() => onAdd(category)}
        disabled={disabled}
        className={cn(
          'h-full min-h-24 w-full flex-col justify-end pr-12',
          getCategoryTileColorOption(category).className
        )}
      >
        <CategoryTileName category={category} />
      </ActionSurface>
      <Button
        variant="outline"
        size="icon-xs"
        className={getCategoryDragHandleClasses(isDragging)}
        aria-label={`Drag ${category.name}`}
        title="Drag to reorder"
        disabled={disabled}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </Button>
      <CategoryTileColorPicker
        category={category}
        disabled={disabled}
        onChangeColor={onChangeColor}
      />
    </div>
  )
}

export function CategoryGridSlot({
  slotIndex,
  isDraggingCategory,
  isPersistedGap,
  children,
}: {
  slotIndex: number
  isDraggingCategory: boolean
  isPersistedGap: boolean
  children?: ReactNode
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: getCategorySlotId(slotIndex),
  })

  return (
    <div
      ref={setNodeRef}
      data-pos-category-grid-slot={slotIndex}
      className={cn(
        'relative min-h-24 rounded-lg transition-colors',
        isPersistedGap && !isDraggingCategory && 'border border-dashed border-transparent',
        isDraggingCategory && 'border border-dashed border-border/45 bg-muted/10',
        isOver && 'border-primary/50 bg-primary/15'
      )}
    >
      {children}
    </div>
  )
}

interface ScrollbarMetrics {
  isScrollable: boolean
  thumbLeft: number
  thumbWidth: number
}

/** A drag of the scroll area's content. */
interface ContentDragState {
  captured: boolean
  didDrag: boolean
  scrollLeft: number
  startX: number
}

/** A drag of the scroll area's scrollbar. */
interface ScrollbarDragState {
  maxScrollLeft: number
  pointerId: number
  startScrollLeft: number
  startX: number
  thumbTravel: number
}

export function DraggableHorizontalScrollArea({
  children,
  variant = 'collection',
}: {
  children: ReactNode
  variant?: 'collection' | 'history'
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const scrollbarTrackRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef<ContentDragState | null>(null)
  const scrollbarDragStateRef = useRef<ScrollbarDragState | null>(null)
  const metricsFrameRef = useRef<number | null>(null)
  const suppressClickRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)
  const [isScrollbarDragging, setIsScrollbarDragging] = useState(false)
  const [scrollbarMetrics, setScrollbarMetrics] = useState<ScrollbarMetrics>({
    isScrollable: false,
    thumbLeft: 0,
    thumbWidth: 0,
  })

  const getViewport = () => viewportRef.current

  const finishDrag = (event: PointerEvent<HTMLDivElement>) => {
    const state = dragStateRef.current

    if (!state) {
      return
    }

    if (state.didDrag) {
      suppressClickRef.current = true
      window.setTimeout(() => {
        suppressClickRef.current = false
      }, 0)
    }

    dragStateRef.current = null
    setIsDragging(false)

    try {
      event.currentTarget.releasePointerCapture(event.pointerId)
    } catch {
      // Pointer capture can already be released if the pointer was canceled.
    }
  }

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    const viewport = getViewport()

    if (!viewport || viewport.scrollWidth <= viewport.clientWidth) {
      return
    }

    const horizontalDelta =
      Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY

    if (horizontalDelta === 0) {
      return
    }

    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth
    const nextScrollLeft = Math.max(
      0,
      Math.min(maxScrollLeft, viewport.scrollLeft + horizontalDelta)
    )

    if (nextScrollLeft === viewport.scrollLeft) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    viewport.scrollLeft = nextScrollLeft
  }

  const dataAttributes =
    variant === 'history'
      ? { 'data-pos-customer-history-carousel': true }
      : { 'data-pos-collection-carousel': true }

  useEffect(() => {
    const viewport = viewportRef.current
    const track = scrollbarTrackRef.current

    if (!viewport || !track) {
      return undefined
    }

    const applyMetrics = (nextMetrics: ScrollbarMetrics) => {
      setScrollbarMetrics((currentMetrics) => {
        if (
          currentMetrics.isScrollable === nextMetrics.isScrollable &&
          Math.abs(currentMetrics.thumbLeft - nextMetrics.thumbLeft) < 0.5 &&
          Math.abs(currentMetrics.thumbWidth - nextMetrics.thumbWidth) < 0.5
        ) {
          return currentMetrics
        }

        return nextMetrics
      })
    }

    const measureMetrics = () => {
      metricsFrameRef.current = null

      const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth
      const trackWidth = track.clientWidth

      if (maxScrollLeft <= 0 || trackWidth <= 0) {
        applyMetrics({
          isScrollable: false,
          thumbLeft: 0,
          thumbWidth: 0,
        })

        return
      }

      const thumbWidth = Math.max(32, (viewport.clientWidth / viewport.scrollWidth) * trackWidth)
      const thumbTravel = Math.max(0, trackWidth - thumbWidth)
      const thumbLeft = maxScrollLeft > 0 ? (viewport.scrollLeft / maxScrollLeft) * thumbTravel : 0

      applyMetrics({
        isScrollable: true,
        thumbLeft,
        thumbWidth,
      })
    }

    const updateMetrics = () => {
      if (metricsFrameRef.current !== null) {
        return
      }

      metricsFrameRef.current = window.requestAnimationFrame(measureMetrics)
    }

    updateMetrics()

    const resizeObserver = new ResizeObserver(updateMetrics)
    resizeObserver.observe(viewport)
    resizeObserver.observe(track)

    if (viewport.firstElementChild) {
      resizeObserver.observe(viewport.firstElementChild)
    }

    viewport.addEventListener('scroll', updateMetrics, { passive: true })
    window.addEventListener('resize', updateMetrics)

    return () => {
      if (metricsFrameRef.current !== null) {
        window.cancelAnimationFrame(metricsFrameRef.current)
        metricsFrameRef.current = null
      }

      resizeObserver.disconnect()
      viewport.removeEventListener('scroll', updateMetrics)
      window.removeEventListener('resize', updateMetrics)
    }
  }, [])

  const startScrollbarDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return
    }

    const viewport = getViewport()
    const track = scrollbarTrackRef.current

    if (!viewport || !track || !scrollbarMetrics.isScrollable) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth
    const thumbTravel = Math.max(1, track.clientWidth - scrollbarMetrics.thumbWidth)
    const trackRect = track.getBoundingClientRect()
    const targetIsThumb =
      event.target instanceof Element &&
      Boolean(event.target.closest('[data-slot="scroll-area-thumb"]'))

    if (!targetIsThumb) {
      const nextThumbLeft = Math.max(
        0,
        Math.min(thumbTravel, event.clientX - trackRect.left - scrollbarMetrics.thumbWidth / 2)
      )
      viewport.scrollLeft = (nextThumbLeft / thumbTravel) * maxScrollLeft
    }

    scrollbarDragStateRef.current = {
      maxScrollLeft,
      pointerId: event.pointerId,
      startScrollLeft: viewport.scrollLeft,
      startX: event.clientX,
      thumbTravel,
    }

    setIsScrollbarDragging(true)
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const moveScrollbarDrag = (event: PointerEvent<HTMLDivElement>) => {
    const state = scrollbarDragStateRef.current
    const viewport = getViewport()

    if (!state || !viewport || state.pointerId !== event.pointerId) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const deltaX = event.clientX - state.startX
    const scrollDelta = (deltaX / state.thumbTravel) * state.maxScrollLeft

    viewport.scrollLeft = Math.max(
      0,
      Math.min(state.maxScrollLeft, state.startScrollLeft + scrollDelta)
    )
  }

  const finishScrollbarDrag = (event: PointerEvent<HTMLDivElement>) => {
    const state = scrollbarDragStateRef.current

    if (!state || state.pointerId !== event.pointerId) {
      return
    }

    scrollbarDragStateRef.current = null
    setIsScrollbarDragging(false)
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }

  return (
    <div
      ref={rootRef}
      {...dataAttributes}
      className="w-full min-w-0 max-w-full overflow-hidden"
      style={{ width: '100%' }}
    >
      <div
        ref={viewportRef}
        data-slot="scroll-area-viewport"
        className={cn(
          'no-scrollbar w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden whitespace-nowrap',
          isDragging &&
            'cursor-grabbing select-none [&_button]:cursor-grabbing [&_[role=button]]:cursor-grabbing'
        )}
        style={{ width: '100%', maxWidth: '100%' }}
        onClickCapture={(event) => {
          if (!suppressClickRef.current) {
            return
          }

          event.preventDefault()
          event.stopPropagation()
        }}
        onDragStartCapture={(event) => {
          event.preventDefault()
        }}
        onPointerCancel={finishDrag}
        onPointerDown={(event) => {
          if (event.button !== 0) {
            return
          }

          const viewport = getViewport()

          if (!viewport || viewport.scrollWidth <= viewport.clientWidth) {
            return
          }

          dragStateRef.current = {
            captured: false,
            didDrag: false,
            scrollLeft: viewport.scrollLeft,
            startX: event.clientX,
          }
        }}
        onPointerMove={(event) => {
          const state = dragStateRef.current
          const viewport = getViewport()

          if (!state || !viewport) {
            return
          }

          const deltaX = event.clientX - state.startX

          if (Math.abs(deltaX) > 4) {
            state.didDrag = true
            setIsDragging(true)

            if (!state.captured) {
              event.currentTarget.setPointerCapture(event.pointerId)
              state.captured = true
            }
          }

          if (state.didDrag) {
            viewport.scrollLeft = state.scrollLeft - deltaX
          }
        }}
        onPointerUp={finishDrag}
        onWheel={handleWheel}
      >
        <div className="inline-flex min-w-max gap-2 pr-4">{children}</div>
      </div>
      <div
        ref={scrollbarTrackRef}
        data-slot="scroll-area-scrollbar"
        data-orientation="horizontal"
        className={cn(
          'mt-1 h-2.5 w-full touch-none rounded-full',
          scrollbarMetrics.isScrollable
            ? isScrollbarDragging
              ? 'cursor-grabbing'
              : 'cursor-grab'
            : 'opacity-0'
        )}
        onPointerCancel={finishScrollbarDrag}
        onPointerDown={startScrollbarDrag}
        onPointerMove={moveScrollbarDrag}
        onPointerUp={finishScrollbarDrag}
      >
        {scrollbarMetrics.isScrollable ? (
          <div
            data-slot="scroll-area-thumb"
            data-testid="pos-horizontal-scrollbar-thumb"
            className={cn(
              'h-full rounded-full bg-border',
              isScrollbarDragging ? 'cursor-grabbing' : 'cursor-grab active:cursor-grabbing'
            )}
            style={{
              transform: `translateX(${scrollbarMetrics.thumbLeft}px)`,
              width: `${scrollbarMetrics.thumbWidth}px`,
            }}
          />
        ) : null}
      </div>
    </div>
  )
}

export function POSCategoryManager({
  categories,
  disabled,
  orgId,
}: {
  categories: Category[]
  disabled: boolean
  orgId: number
}) {
  const [open, setOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [categoryNameDrafts, setCategoryNameDrafts] = useState<Record<number, string>>({})
  const createCategoryMutation = useCreateCategoryMutation()
  const updateCategoryMutation = useUpdateCategoryMutation({ invalidateOnSuccess: false })
  const isPending = createCategoryMutation.isPending || updateCategoryMutation.isPending || disabled
  const sortedCategories = sortPosCategories(categories)
  const categoryQueryKey = categoryQueryKeys.all({ organizationId: orgId })

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    setCategoryNameDrafts({})
  }

  const updateCategory = async (category: Category, updates: UpdateCategoryInput) => {
    const previousCategorySnapshots =
      queryClient.getQueriesData?.({
        queryKey: categoryQueryKey,
      }) ?? []

    queryClient.setQueriesData?.(
      {
        queryKey: categoryQueryKey,
      },
      (data) => updateCategoryQueryData(data, category.id, updates)
    )

    try {
      await updateCategoryMutation.mutateAsync({
        id: category.id,
        ...updates,
      })
    } catch (error) {
      previousCategorySnapshots.forEach(([queryKey, data]) => {
        queryClient.setQueryData?.(queryKey, data)
      })
      void queryClient.invalidateQueries?.({
        queryKey: categoryQueryKey,
      })
      toast.error('Failed to update shortcut', {
        description: getErrorMessage(error) || 'Please try again.',
      })
    }
  }

  const commitCategoryNameDrafts = () => {
    sortedCategories.forEach((category) => {
      const draftName = categoryNameDrafts[category.id]

      if (draftName === undefined) {
        return
      }

      const name = draftName.trim()

      if (!name || name === category.name) {
        return
      }

      void updateCategory(category, { name })
    })
  }

  const handleDone = () => {
    commitCategoryNameDrafts()
    setCategoryNameDrafts({})
    setOpen(false)
  }

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name) return
    const posSortOrder = getFirstAvailableCategorySortOrder(sortedCategories)

    try {
      await createCategoryMutation.mutateAsync({
        name,
        show_in_pos: true,
        pos_sort_order: posSortOrder,
        pos_color: getCategoryColorValue(posSortOrder),
      })
      setNewName('')
    } catch (error) {
      toast.error('Failed to create shortcut', {
        description: getErrorMessage(error) || 'Please try again.',
      })
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        aria-label="Manage quick-add shortcuts"
        onClick={() => handleOpenChange(true)}
      >
        <Settings2 className="h-4 w-4" />
        <span aria-hidden="true" className="@[28rem]:hidden">
          Manage
        </span>
        <span aria-hidden="true" className="hidden @[28rem]:inline">
          Manage Shortcuts
        </span>
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          viewport="contained"
          className="flex! max-h-[min(calc(100vh-2rem),44rem)] flex-col"
        >
          <DialogHeader className="shrink-0">
            <DialogTitle>Quick-add Shortcuts</DialogTitle>
            <DialogDescription>
              Choose which shortcut buttons appear on POS order entry.
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <div className="flex shrink-0 gap-2">
              <Input
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    void handleCreate()
                  }
                }}
                placeholder="New shortcut..."
                disabled={isPending}
              />
              <Button type="button" onClick={handleCreate} disabled={isPending || !newName.trim()}>
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>

            <ScrollArea
              className="-mr-3 h-[min(28rem,calc(100vh-18rem))] pr-3"
              viewportStyle={{ height: 'min(28rem, calc(100vh - 18rem))', overflowY: 'auto' }}
            >
              <div className="space-y-2">
                {sortedCategories.map((category) => (
                  <div key={category.id} className="flex items-center gap-2 rounded-lg border p-2">
                    <Input
                      value={categoryNameDrafts[category.id] ?? category.name}
                      disabled={isPending}
                      onChange={(event) => {
                        setCategoryNameDrafts((drafts) => ({
                          ...drafts,
                          [category.id]: event.target.value,
                        }))
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          handleDone()
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant={category.show_in_pos ? 'secondary' : 'outline'}
                      size="sm"
                      className="shrink-0 gap-2"
                      disabled={isPending}
                      onClick={() => {
                        const updates: UpdateCategoryInput = { show_in_pos: !category.show_in_pos }

                        if (!category.show_in_pos) {
                          const currentSortOrder = getCategorySortOrder(category, -1)
                          const posSortOrder =
                            currentSortOrder >= 0 &&
                            isCategorySortOrderOpen(sortedCategories, currentSortOrder, category.id)
                              ? currentSortOrder
                              : getFirstAvailableCategorySortOrder(sortedCategories)

                          updates.pos_sort_order = posSortOrder

                          if (!category.pos_color) {
                            updates.pos_color = getCategoryColorValue(posSortOrder)
                          }
                        }

                        void updateCategory(category, updates)
                      }}
                    >
                      {category.show_in_pos ? (
                        <Eye className="h-4 w-4" />
                      ) : (
                        <EyeOff className="h-4 w-4" />
                      )}
                      {category.show_in_pos ? 'Shown' : 'Hidden'}
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="flex shrink-0 justify-end border-t pt-3">
              <Button type="button" onClick={handleDone} disabled={disabled} className="min-w-24">
                OK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
